import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MessageSquare, Sparkles } from "lucide-react";
import { SERVICES, SPA_SERVICES, IMAGES } from "../data/site";
import { SectionHeading } from "../components/SectionHeading";
import { Seo } from "../components/Seo";
import { SEO, itemListSchema, breadcrumbSchema, SITE_URL } from "../data/seo";
import { SpaServicesSection } from "../components/SpaServicesSection";

export const ServicesHub = () => (
  <div data-testid="services-page">
    <Seo
      title={SEO.services.title}
      description={SEO.services.description}
      schemas={[
        itemListSchema(
          "Dental Services",
          SERVICES.map((s) => ({
            name: s.name,
            url: `${SITE_URL}/services/${s.slug}`,
          })),
        ),
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
        ]),
      ]}
    />
    <section className="relative h-[70vh] min-h-[500px] overflow-hidden">
      <img
        src={IMAGES.clinicInterior3}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-ink/60" />
      <div className="relative h-full container-wide flex flex-col justify-end pb-20 text-white">
        <div className="label-eyebrow text-gold mb-6">Services</div>
        <h1 className="heading-display text-5xl md:text-7xl lg:text-8xl max-w-3xl text-white">
          Dentistry &<span className="italic text-gold-light">Aesthetics.</span>
        </h1>
        <p className="text-white/70 mt-8 max-w-xl text-lg">
          From a single shade of whitening to a full reimagining of your smile —
          every service is delivered with the same standard of craft.
        </p>
      </div>
    </section>

    <section className="py-24 md:py-32 container-x">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-ink/10">
        {SERVICES.map((s, i) => (
          <Link
            key={s.slug}
            to={`/services/${s.slug}`}
            data-testid={`services-hub-card-${s.slug}`}
            className="group relative bg-bone p-10 md:p-14 overflow-hidden reveal"
            style={{ transitionDelay: `${i * 70}ms` }}
          >
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8 items-center">
              <div className="md:col-span-2 aspect-[4/5] overflow-hidden">
                <img
                  src={s.image}
                  alt={s.name}
                  className="w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-110"
                />
              </div>
              <div className="md:col-span-3">
                <div className="label-eyebrow mb-4">
                  {String(i + 1).padStart(2, "0")} — {s.category}
                </div>
                <h3 className="font-serif text-3xl md:text-4xl font-light leading-tight group-hover:text-gold transition-colors">
                  {s.name}
                </h3>
                <p
                  className="mt-4 text-ink/70 leading-relaxed overflow-hidden"
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 10,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {s.summary}
                </p>
                <div className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase border-b border-ink pb-1 group-hover:text-gold group-hover:border-gold transition-colors">
                  Read More{" "}
                  <ArrowUpRight
                    size={14}
                    strokeWidth={1.4}
                    className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                  />
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>

    {/* Text Our Team */}
    <section className="py-24 bg-ink text-bone text-center">
      <div className="container-x">
        <MessageSquare
          size={32}
          strokeWidth={1}
          className="text-gold mx-auto mb-6"
        />
        <SectionHeading
          title="Have a quick"
          italicWord="question?"
          align="center"
          light
        />
        <p className="text-white/60 mt-6 max-w-md mx-auto">
          Text our team. We respond within a few business hours — usually
          faster.
        </p>
        <a
          href="sms:+15166212430"
          data-testid="text-team-link"
          className="btn-outline-light mt-10 inline-flex"
        >
          Text Our Team
        </a>
      </div>
    </section>

    {/* Spa Services */}
    <section
      className="py-24 md:py-32 container-x bg-bone"
      data-testid="services-spa-section"
    >
      <div className="flex items-center gap-4 mb-6 reveal">
        <Sparkles size={20} strokeWidth={1.2} className="text-gold" />
        <span className="label-eyebrow">Exclusive Spa</span>
      </div>
      <SpaServicesSection services={SPA_SERVICES} />
    </section>
    {/* <section className="py-24 md:py-32 container-x bg-bone" data-testid="services-spa-section">
      <div className="flex items-center gap-4 mb-6 reveal">
        <Sparkles size={20} strokeWidth={1.2} className="text-gold" />
        <span className="label-eyebrow">Exclusive Spa</span>
      </div>
      <SectionHeading title="And a wellness studio," italicWord="under the same roof." />
      <p className="max-w-2xl text-ink/70 mt-6 reveal">A first in dentistry — recover, recharge, and glow in our in-office wellness spa. Available standalone or as a complement to your dental treatment.</p>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 lg:max-w-6xl lg:mx-auto">
        {SPA_SERVICES.map((s, i) => (
          <div key={s.slug} className="group bg-cream p-8 md:p-10 reveal flex flex-col" data-testid={`services-spa-${s.slug}`} style={{ transitionDelay: `${i * 60}ms` }}>
            <div className="aspect-[4/3] overflow-hidden mb-6">
              <img src={s.image} alt={s.name} className="w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-110" />
            </div>
            <h3 className="font-serif text-2xl font-light leading-tight">{s.name}</h3>
            <p className="font-serif italic text-gold-dark mt-2 text-sm">{s.tagline}</p>
            <p className="text-ink/60 text-sm mt-4 leading-relaxed flex-1">{s.summary}</p>
          </div>
        ))}
      </div>
    </section> */}
  </div>
);
